'use client';

import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { MobileTopBar } from '@/app/components/layouts/top-navigation/shared/mobile-top-bar';
import { SubMenuRenderLayout } from '@/app/components/layouts/top-navigation/sub-menu/render-layout';
import { useMountTransition } from '@/app/hooks/useMountTransition';
import type { NavItemType } from '@/app/types/top-navigation';
import { cn } from '@/app/utils/cn';
import { safeCall } from '@/app/utils/safeCall';

const Backdrop = dynamic(
  () =>
    import('@/app/components/overlays/backdrop').then((mod) => mod.Backdrop),
  {
    ssr: false,
  }
);

interface SubMenuProps {
  logo?: ReactNode;
  isOpen?: boolean;
  activeItem?: NavItemType;
  isDesktop?: boolean;
  onSetActiveItem?: (item?: NavItemType) => void;
  onClose?: () => void;
}

const SUBMENU_ID = 'menu_submenu';

export const SubMenu = ({
  logo,
  isOpen,
  activeItem,
  isDesktop = false,
  onSetActiveItem,
  onClose,
}: SubMenuProps) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [desktopActiveItem, setDesktopActiveItem] = useState<
    NavItemType | undefined
  >(undefined);
  const [isHover, setIsHover] = useState<boolean>(false);
  const [hasContentMounted, setHasContentMounted] = useState<boolean>(false);

  const {
    className: transitionClass,
    transitionState,
    isTransitioning,
  } = useMountTransition({
    isMount: !!isOpen && !!hasContentMounted,
    targetId: SUBMENU_ID,
    ref: menuRef,
    animateClassName: 'opacity-100',
    baseClassName: 'opacity-0 transition-opacity duration-300 ease-in-out',
  });

  const clearSelectItem = () => {
    safeCall(onSetActiveItem, undefined);
  };

  const closeActiveMenu = () => {
    safeCall(onClose);
    if (!isDesktop) clearSelectItem();
    else setIsHover(false);
  };

  // Note: To still show active item on closing even though the `activeItem` is removed
  useEffect(() => {
    if (!isDesktop) return;

    if (isOpen && activeItem) {
      setDesktopActiveItem(activeItem);
    }

    if (!isOpen) {
      setHasContentMounted(false);
      setDesktopActiveItem(undefined);
    }
  }, [isOpen, activeItem, isDesktop]);

  // MOBILE
  if (!isDesktop) {
    return (
      <>
        <MobileTopBar
          logo={logo}
          activeLabel={activeItem?.label}
          onBack={clearSelectItem}
          onClose={closeActiveMenu}
        />
        <div className="absolute inset-0 top-14 w-full overflow-y-auto bg-white">
          <SubMenuRenderLayout
            activeItem={activeItem}
            onReady={() => {
              if (isOpen) setHasContentMounted(true);
            }}
            onClose={onClose}
          />
        </div>
      </>
    );
  }

  // DESKTOP
  if (!isTransitioning && !desktopActiveItem) return null;

  return (
    <>
      {transitionState !== 'exit' && (
        <Backdrop
          className={cn('fixed inset-0', transitionClass, {
            'duration-800': transitionState === 'animate',
            'pointer-events-none opacity-0': !desktopActiveItem,
          })}
          disabledAnimate
          onClick={closeActiveMenu}
          onMouseEnter={isHover ? closeActiveMenu : undefined}
        />
      )}
      <aside
        ref={menuRef}
        id={SUBMENU_ID}
        className={cn(
          'absolute top-0 w-full bg-white pt-24 pb-10',
          {
            'min-h-94': !!desktopActiveItem,
            'pointer-events-none opacity-0': !desktopActiveItem,
          },
          transitionClass
        )}
        onMouseEnter={isOpen ? () => setIsHover(true) : undefined}
      >
        <SubMenuRenderLayout
          activeItem={desktopActiveItem}
          isDesktop
          onReady={() => {
            if (isOpen) setHasContentMounted(true);
          }}
          onClose={closeActiveMenu}
        />
      </aside>
    </>
  );
};
